import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import AppointmentList from './Appointments/AppointmentList';
import AppointmentDetails from './Appointments/AppointmentDetails';
import AppointmentStatusUpdate from './Appointments/AppointmentStatusUpdate';
import AppointmentForm from './Appointments/AppointmentForm';
import SearchableSelect from './common/SearchableSelect';

const Appointments = () => {
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('all'); // Default tab is 'today'
  
  // Add new state variables for status update
  const [updateAppointment, setUpdateAppointment] = useState(null);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [updateError, setUpdateError] = useState(null);
  const [updateSuccess, setUpdateSuccess] = useState(false);

  // Add new state for filters
  const [doctors, setDoctors] = useState([]);
  const [patients, setPatients] = useState([]);
  const [doctorFilter, setDoctorFilter] = useState('');
  const [patientFilter, setPatientFilter] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Add state for new appointment form
  const [showAppointmentForm, setShowAppointmentForm] = useState(false);
  const [addSuccess, setAddSuccess] = useState(false);

  useEffect(() => {
    const fetchAppointments = async () => {
      try {
        setLoading(true);
        const response = await api.get('/api/appointments');
        setAppointments(response.data);
        setError(null);

        // Extract unique doctors and patients for filters
        const uniqueDoctors = [];
        const uniquePatients = [];
        const doctorMap = new Map();
        const patientMap = new Map();

        response.data.forEach(appointment => {
          // Add doctor to unique doctors if not already present
          const doctorId = appointment.doctor?._id || appointment.doctor;
          const doctorName = appointment.doctor?.name || appointment.doctorName;
          if (doctorId && doctorName && !doctorMap.has(doctorId)) {
            doctorMap.set(doctorId, true);
            uniqueDoctors.push({ id: doctorId, name: doctorName });
          }

          // Add patient to unique patients if not already present
          const patientId = appointment.patient?._id || appointment.patient;
          const patientName = appointment.patient?.name || appointment.patientName;
          if (patientId && patientName && !patientMap.has(patientId)) {
            patientMap.set(patientId, true);
            uniquePatients.push({ id: patientId, name: patientName });
          }
        });

        setDoctors(uniqueDoctors);
        setPatients(uniquePatients);
      } catch (err) {
        console.error('Error fetching appointments:', err);
        setError('Failed to fetch appointments. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchAppointments();
  }, []);

  const handleViewAppointment = async (appointmentId) => {
    try {
      setViewLoading(true);
      
      // First try to find the appointment in our existing data
      const existingAppointment = appointments.find(a => 
        (a._id && a._id === appointmentId) || (a.id && a.id === appointmentId)
      );
      
      if (existingAppointment && existingAppointment.doctor?.name) {
        // If we already have the full data, use it
        setSelectedAppointment(existingAppointment);
      } else {
        // Otherwise, fetch the detailed appointment data
        const response = await api.get(`/api/appointments/${appointmentId}`);
        setSelectedAppointment(response.data);
      }
    } catch (err) {
      console.error('Error fetching appointment details:', err);
    } finally {
      setViewLoading(false);
    }
  };

  const closeAppointmentDetails = () => {
    setSelectedAppointment(null);
  };

  const handleUpdateAppointment = (appointment) => {
    setUpdateAppointment(appointment);
  };

  const closeUpdateModal = () => {
    setUpdateAppointment(null);
    setUpdateError(null);
    setUpdateSuccess(false);
  };

  const submitStatusUpdate = async (appointmentId, newStatus) => {
    try {
      setUpdateLoading(true);
      setUpdateError(null);
      
      // Make API call to update appointment status
      const response = await api.put(`/api/appointments/${appointmentId}/status`, {
        status: newStatus
      });
      
      // Update the appointments list with the updated appointment
      setAppointments(appointments.map(app => 
        (app._id === appointmentId || app.id === appointmentId) 
          ? { ...app, status: newStatus } 
          : app
      ));
      
      setUpdateSuccess(true);
      
      // Close the modal after a short delay
      setTimeout(() => {
        closeUpdateModal();
      }, 1500);
      
    } catch (err) {
      console.error('Error updating appointment status:', err);
      setUpdateError('Failed to update appointment status. Please try again.');
    } finally {
      setUpdateLoading(false);
    }
  };

  const handleDeleteAppointment = async (appointmentId) => {
    if (window.confirm('Are you sure you want to delete this appointment?')) {
      try {
        setLoading(true);
        await api.delete(`/api/appointments/${appointmentId}`);
        setAppointments(appointments.filter(app => app._id !== appointmentId && app.id !== appointmentId));
      } catch (err) {
        console.error('Error deleting appointment:', err);
        setError('Failed to delete appointment. Please try again.');
      } finally {
        setLoading(false);
      }
    }
  };

  // Helper function to check if a date is today
  const isToday = (dateString) => {
    const today = new Date();
    const appointmentDate = new Date(dateString);
    
    return appointmentDate.getDate() === today.getDate() &&
           appointmentDate.getMonth() === today.getMonth() &&
           appointmentDate.getFullYear() === today.getFullYear();
  };

  // Helper function to check if a date is in the future (but not today)
  const isUpcoming = (dateString) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Set to start of day for comparison
    
    const appointmentDate = new Date(dateString);
    appointmentDate.setHours(0, 0, 0, 0); // Set to start of day for comparison
    
    return appointmentDate > today;
  };

  // Handle date change
  const handleDateChange = (e) => {
    setDateFilter(e.target.value);
  };

  // Reset all filters
  const resetFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setDoctorFilter('');
    setPatientFilter('');
    setDateFilter('');
  };

  // Filter appointments based on all criteria
  const filteredAppointments = appointments.filter(appointment => {
    const patientName = appointment.patient?.name || appointment.patientName || '';
    const doctorName = appointment.doctor?.name || appointment.doctorName || '';
    const patientId = appointment.patient?._id || appointment.patient || '';
    const doctorId = appointment.doctor?._id || appointment.doctor || '';
    
    // Search term filter
    const matchesSearch = patientName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         doctorName.toLowerCase().includes(searchTerm.toLowerCase());
    
    // Status filter
    const matchesStatus = statusFilter === 'all' || appointment.status === statusFilter;
    
    // Doctor filter
    const matchesDoctor = !doctorFilter || doctorId === doctorFilter;
    
    // Patient filter
    const matchesPatient = !patientFilter || patientId === patientFilter;
    
    // Date filter
    const matchesDate = !dateFilter || new Date(appointment.date).toISOString().split('T')[0] === dateFilter;
    
    // Tab filter
    let matchesTab = true;
    if (activeTab === 'today') {
      matchesTab = isToday(appointment.date);
    } else if (activeTab === 'upcoming') {
      matchesTab = isUpcoming(appointment.date);
    } else if (activeTab === 'cancelled') {
      matchesTab = appointment.status === 'cancelled';
    } else if (activeTab === 'all') {
      matchesTab = true; // Show all appointments
    }
    
    return matchesSearch && matchesStatus && matchesDoctor && matchesPatient && matchesDate && matchesTab;
  });

  // Handler for successful appointment creation
  const handleAppointmentCreated = () => {
    // Close the form
    setShowAppointmentForm(false);
    
    // Show success message
    setAddSuccess(true);
    
    // Refresh the appointments list
    const fetchAppointments = async () => {
      try {
        setLoading(true);
        const response = await api.get('/api/appointments');
        setAppointments(response.data);
        
        // Also update the doctors and patients lists
        const uniqueDoctors = [];
        const uniquePatients = [];
        const doctorMap = new Map();
        const patientMap = new Map();

        response.data.forEach(appointment => {
          // Add doctor to unique doctors if not already present
          const doctorId = appointment.doctor?._id || appointment.doctor;
          const doctorName = appointment.doctor?.name || appointment.doctorName;
          if (doctorId && doctorName && !doctorMap.has(doctorId)) {
            doctorMap.set(doctorId, true);
            uniqueDoctors.push({ id: doctorId, name: doctorName });
          }

          // Add patient to unique patients if not already present
          const patientId = appointment.patient?._id || appointment.patient;
          const patientName = appointment.patient?.name || appointment.patientName;
          if (patientId && patientName && !patientMap.has(patientId)) {
            patientMap.set(patientId, true);
            uniquePatients.push({ id: patientId, name: patientName });
          }
        });

        setDoctors(uniqueDoctors);
        setPatients(uniquePatients);
      } catch (err) {
        console.error('Error refreshing appointments:', err);
      } finally {
        setLoading(false);
        
        // Hide success message after 3 seconds
        setTimeout(() => {
          setAddSuccess(false);
        }, 3000);
      }
    };

    fetchAppointments();
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-8 flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Appointments Management</h1>
          <p className="text-gray-600 mt-1">View and manage patient appointments</p>
        </div>
        {/* <button 
          onClick={() => setShowAppointmentForm(true)}
          className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
        >
          <div className="flex items-center">
            <svg 
              xmlns="http://www.w3.org/2000/svg" 
              className="h-5 w-5 mr-2" 
              fill="none" 
              viewBox="0 0 24 24" 
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add Appointment
          </div>
        </button> */}
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-md">
          <p>{error}</p>
        </div>
      )}

      {addSuccess && (
        <div className="mb-6 p-4 bg-green-100 border border-green-400 text-green-700 rounded-md">
          <p>Appointment created successfully!</p>
        </div>
      )}

      {updateSuccess && (
        <div className="mb-6 p-4 bg-green-100 border border-green-400 text-green-700 rounded-md">
          <p>Appointment status updated successfully!</p>
        </div>
      )}
      
      {updateError && (
        <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-md">
          <p>{updateError}</p>
        </div>
      )}

      {/* Primary Filters */}
      <div className="mb-6 flex flex-col sm:flex-row gap-4">
        <div className="flex-1">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <input
              type="text"
              placeholder="Search by patient or doctor name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-md w-full focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
        </div>
        <div className="sm:w-48">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="w-full border border-gray-300 rounded-md py-2 pl-3 pr-10 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="all">All Status</option>
            <option value="confirmed">Confirmed</option>
            <option value="pending">Pending</option>
            <option value="cancelled">Cancelled</option>
            <option value="completed">Completed</option>
          </select>
        </div>
      </div>
      
      {/* Additional Filters */}
      <div className="mb-6 flex flex-col sm:flex-row gap-4">
        {/* Doctor Filter */}
        <div className="flex-1">
          <SearchableSelect
            options={doctors}
            value={doctorFilter}
            onChange={setDoctorFilter}
            placeholder="All Doctors"
            className="w-full py-1"
          />
        </div>
        
        {/* Patient Filter */}
        <div className="flex-1">
          <SearchableSelect
            options={patients}
            value={patientFilter}
            onChange={setPatientFilter}
            placeholder="All Patients"
            className="w-full py-1"
          />
        </div>
        
        {/* Date Filter */}
        <div className="flex-1">
          <input
            type="date"
            value={dateFilter}
            onChange={handleDateChange}
            className="w-full border border-gray-300 rounded-md py-2 px-3 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>
        
        {/* Reset button */}
        <div>
          <button
            onClick={resetFilters}
            className="w-full px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500"
          >
            Reset Filters
          </button>
        </div>
      </div>
      
      {/* Tab Navigation */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('all')}
            className={`whitespace-nowrap pb-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'all'
                ? 'border-primary-600 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
            aria-current={activeTab === 'all' ? 'page' : undefined}
          >
            All Appointments
          </button>
          <button
            onClick={() => setActiveTab('today')}
            className={`whitespace-nowrap pb-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'today'
                ? 'border-primary-600 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
            aria-current={activeTab === 'today' ? 'page' : undefined}
          >
            Today's Appointments
          </button>
          <button
            onClick={() => setActiveTab('upcoming')}
            className={`whitespace-nowrap pb-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'upcoming'
                ? 'border-primary-600 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
            aria-current={activeTab === 'upcoming' ? 'page' : undefined}
          >
            Upcoming Appointments
          </button>
          <button
            onClick={() => setActiveTab('cancelled')}
            className={`whitespace-nowrap pb-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'cancelled'
                ? 'border-primary-600 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
            aria-current={activeTab === 'cancelled' ? 'page' : undefined}
          >
            Cancelled Appointments
          </button>
        </nav>
      </div>

      {/* Appointments Table */}
      <AppointmentList 
        appointments={filteredAppointments} 
        loading={loading}
        onViewAppointment={handleViewAppointment}
        onUpdateAppointment={handleUpdateAppointment}
        onDeleteAppointment={handleDeleteAppointment}
      />

      {/* Appointment Details Modal */}
      {selectedAppointment && (
        <AppointmentDetails
          appointment={selectedAppointment}
          onClose={closeAppointmentDetails}
        />
      )}

      {/* Appointment Status Update Modal */}
      {updateAppointment && (
        <AppointmentStatusUpdate
          appointment={updateAppointment}
          onClose={closeUpdateModal}
          onUpdate={submitStatusUpdate}
          loading={updateLoading}
        />
      )}

      {/* New Appointment Form Modal */}
      {showAppointmentForm && (
        <AppointmentForm
          onClose={() => setShowAppointmentForm(false)}
          onSuccess={handleAppointmentCreated}
        />
      )}

      {/* Loading overlay for appointment details */}
      {viewLoading && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
          <div className="flex flex-col items-center bg-white p-5 rounded-lg shadow-lg">
            <div className="w-12 h-12 border-t-2 border-b-2 border-primary-600 rounded-full animate-spin"></div>
            <p className="mt-4 text-gray-600">Loading appointment details...</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default Appointments;
